import { GameResultHistoryItem, GameResultSynthesis, GameSynthesisStep } from '@orientaction/types';

export const RANK_ORDER = [1, 2, 3];

export const formatRealizationDate = (value?: string) => {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  const datePart = date.toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  const timePart = date.toLocaleTimeString('fr-FR', {
    hour: '2-digit',
    minute: '2-digit',
  });
  return `${datePart} à ${timePart}`;
};

export const formatDuration = (durationSeconds?: number) => {
  if (durationSeconds == null || durationSeconds < 0) {
    return '—';
  }
  if (durationSeconds < 60) {
    return `${Math.round(durationSeconds)} s`;
  }
  return `${Math.round(durationSeconds / 60)} min`;
};

export const getRecapLabel = (rank: number) =>
  rank === 1 ? '1ère tentative' : `${rank}ème tentative`;

export const getEssayLabel = (rank: number) => (rank === 1 ? '1er essai' : `${rank}ème essai`);

export const getEssayCountLabel = (rank: number) =>
  rank > 1 ? `${rank} essais` : `${rank} essai`;

export const getGroupTitle = (count: number, rank: number) => {
  const answers = count > 1 ? 'bonnes réponses' : 'bonne réponse';
  return `${count} ${answers} au ${getEssayLabel(rank)}`;
};

export const getPointsPerStepLabel = (points: number) =>
  `${points} point${points > 1 ? 's' : ''} par étape`;

export const getRetainedAttempt = (step: GameSynthesisStep) =>
  step.attempts.find(attempt => attempt.isCorrect) || step.attempts[step.attempts.length - 1];

export const groupStepsBySuccessRank = (steps: GameSynthesisStep[]) => {
  const grouped = new Map<number, GameSynthesisStep[]>();

  steps.forEach(step => {
    const rank = step.successRank || step.attemptCount || 0;
    if (!rank) {
      return;
    }
    const current = grouped.get(rank) || [];
    grouped.set(rank, [...current, step]);
  });

  return Array.from(grouped.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([rank, rankSteps]) => ({
      rank,
      steps: [...rankSteps].sort((a, b) => a.stepNumber - b.stepNumber),
      points: rankSteps[0]?.points || 0,
    }))
    .filter(group => group.steps.length > 0);
};

export const unwrapSynthesis = (payload: unknown): GameResultSynthesis | null => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const source = payload as { idResult?: number; data?: unknown };
  if (source.idResult) {
    return payload as GameResultSynthesis;
  }

  if (source.data && typeof source.data === 'object' && (source.data as { idResult?: number }).idResult) {
    return source.data as GameResultSynthesis;
  }

  return null;
};

export const unwrapHistory = (response: unknown): GameResultHistoryItem[] => {
  const source = Array.isArray(response)
    ? response
    : Array.isArray((response as { data?: unknown })?.data)
    ? (response as { data: unknown[] }).data
    : Array.isArray((response as { results?: unknown })?.results)
    ? (response as { results: unknown[] }).results
    : [];

  return source
    .map((item: any) => ({
      idResult: Number(item?.idResult ?? item?.id),
      idGame: item?.idGame,
      completedAt: item?.completedAt || item?.startedAt || item?.createdAt || item?.date,
      startedAt: item?.startedAt,
      createdAt: item?.createdAt,
      scoreGlobal: item?.scoreGlobal,
      percentage: item?.percentage,
    }))
    .filter(item => Number.isFinite(item.idResult) && item.idResult > 0);
};
